import React, { useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, CopyIcon, EyeIcon, EyeOffIcon, PencilIcon, PlusIcon, SearchIcon, Trash2Icon, AlertTriangleIcon, CheckIcon } from 'lucide-react';
import { CateringItem, formatPrice } from '../../data/smaken';
import { Partner, newItemId, usePlatform } from '../../contexts/PlatformContext';
import { ProductEditor } from './ProductEditor';
interface Editing {
  item: CateringItem;
  isNew: boolean;
}
const iconButton = 'flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-30 disabled:hover:bg-transparent';

/** One partner's assortment: menu sections on the left, products grouped by section. */
export function MenuEditor({
  partner
}: {
  partner: Partner;
}) {
  const platform = usePlatform();
  const slug = partner.place.slug;
  const [editing, setEditing] = useState<Editing | null>(null);
  const [query, setQuery] = useState('');
  const [newSection, setNewSection] = useState('');
  const [renaming, setRenaming] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const extra = partner.items.map(i => i.section).filter(s => !partner.sections.includes(s));
    return [...partner.sections, ...new Set(extra)].map(section => ({
      section,
      items: partner.items.filter(i => i.section === section && (!q || `${i.name} ${i.description}`.toLowerCase().includes(q))),
      total: partner.items.filter(i => i.section === section).length
    }));
  }, [partner, query]);
  const missingAllergens = partner.items.filter(i => i.allergens === undefined).length;
  const startNew = (section: string) => setEditing({
    isNew: true,
    item: {
      id: newItemId(slug),
      name: '',
      section,
      category: 'Mat',
      occasions: [],
      description: '',
      price: 0,
      unit: 'per styck',
      minGuests: 1,
      leadTime: 1,
      image: ''
    }
  });
  return <div className="grid gap-8 xl:grid-cols-[15rem,minmax(0,1fr)]" data-id="element-113" data-mp="129snm60tovmr">
      {/* Sections */}
      <aside className="xl:sticky xl:top-28 xl:self-start" data-id="element-114" data-mp="1g927bfo8m7vh">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-115" data-mp="1g6foukzt67yh">Menydelar</p>
        <ul className="mt-3 space-y-1" data-id="element-116" data-mp="1bbsa3dulwrx3">
          {partner.sections.map((section, index) => {
          const count = partner.items.filter(i => i.section === section).length;
          const isRenaming = renaming?.from === section;
          return <li key={section} className="group flex items-center gap-1 rounded-xl bg-white px-2 py-1.5" data-id="element-117" data-mp="1yxyqane6pdye" data-mpi={index}>
                {isRenaming ? <form className="flex flex-1 items-center gap-1" onSubmit={e => {
              e.preventDefault();
              platform.renameSection(slug, section, renaming.to);
              setRenaming(null);
            }} data-id="element-118" data-mp="1u2jf637k9w5o">
                    <input autoFocus value={renaming.to} onChange={e => setRenaming({
                from: section,
                to: e.target.value
              })} onBlur={() => setRenaming(null)} className="min-w-0 flex-1 rounded-lg border border-gray-200 px-2 py-1 text-sm outline-none focus:border-black" data-id="element-119" data-mp="2rm9vcn4qbxvu" />
                    <button type="submit" onMouseDown={e => e.preventDefault()} className={iconButton} aria-label="Spara namn" data-id="element-120" data-mp="28nfhrhh053h9">
                      <CheckIcon className="h-3.5 w-3.5" data-id="element-121" data-mp="1uwsjfqf5lflp" />
                    </button>
                  </form> : <>
                    <a href={`#del-${index}`} className="min-w-0 flex-1 truncate px-1 text-sm font-medium text-gray-900" data-id="element-122" data-mp="1kxl4wzwonex0">
                      {section} <span className="text-gray-400" data-id="element-123" data-mp="1pvcx7v18p31q">{count}</span>
                    </a>
                    <div className="flex opacity-100 transition-opacity xl:opacity-0 xl:group-hover:opacity-100 xl:group-focus-within:opacity-100" data-id="element-124" data-mp="2t6a6jonhsuuu">
                      <button className={iconButton} aria-label={`Flytta ${section} upp`} disabled={index === 0} onClick={() => platform.moveSection(slug, section, -1)} data-id="element-125" data-mp="2oal3hbc9kvts">
                        <ArrowUpIcon className="h-3.5 w-3.5" data-id="element-126" data-mp="2ehw6flhgnkhz" />
                      </button>
                      <button className={iconButton} aria-label={`Flytta ${section} ned`} disabled={index === partner.sections.length - 1} onClick={() => platform.moveSection(slug, section, 1)} data-id="element-127" data-mp="29k437vbd8byh">
                        <ArrowDownIcon className="h-3.5 w-3.5" data-id="element-128" data-mp="20jfqd3xpkhn6" />
                      </button>
                      <button className={iconButton} aria-label={`Byt namn på ${section}`} onClick={() => setRenaming({
                  from: section,
                  to: section
                })} data-id="element-129" data-mp="1vkuzvw89dcbx">
                        <PencilIcon className="h-3.5 w-3.5" data-id="element-130" data-mp="3pxksmamw4fhq" />
                      </button>
                      <button className={iconButton} aria-label={`Ta bort ${section}`} disabled={count > 0} title={count > 0 ? 'Flytta eller ta bort produkterna först' : undefined} onClick={() => platform.deleteSection(slug, section)} data-id="element-131" data-mp="3pvrnizrxe3bu">
                        <Trash2Icon className="h-3.5 w-3.5" data-id="element-132" data-mp="fzu65ja5jxy2" />
                      </button>
                    </div>
                  </>}
              </li>;
        })}
        </ul>
        <form className="mt-3 flex gap-2" onSubmit={e => {
        e.preventDefault();
        platform.addSection(slug, newSection);
        setNewSection('');
      }} data-id="element-133" data-mp="3jjf0w1vs6hyz">
          <input value={newSection} onChange={e => setNewSection(e.target.value)} placeholder="Ny menydel" className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-black" data-id="element-134" data-mp="13q6gt1ws88sc" />
          <button type="submit" aria-label="Lägg till menydel" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-white" data-id="element-135" data-mp="34u8nomdip506">
            <PlusIcon className="h-4 w-4" data-id="element-136" data-mp="3drsqw0uxxbdf" />
          </button>
        </form>
      </aside>

      {/* Products */}
      <div className="min-w-0" data-id="element-137" data-mp="39sa7xamojad8">
        <div className="flex flex-wrap items-center gap-3" data-id="element-138" data-mp="2m1xu5j4r6jeq">
          <div className="relative min-w-[14rem] flex-1" data-id="element-139" data-mp="2m2g057h7wbzu">
            <SearchIcon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-140" data-mp="2h3d0k2k0axz9" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder={`Sök bland ${partner.items.length} produkter`} className="w-full rounded-full border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-black" data-id="element-141" data-mp="2uuhuqp4luy27" />
          </div>
          <button onClick={() => startNew(partner.sections[0] ?? 'Övrigt')} className="flex items-center gap-2 rounded-full bg-black px-5 py-2.5 text-sm font-medium text-white" data-id="element-142" data-mp="1yc3ih4w670w2">
            <PlusIcon className="h-4 w-4" data-id="element-143" data-mp="1t0ycklisugoe" /> Ny produkt
          </button>
        </div>

        {missingAllergens > 0 && <p className="mt-4 flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800" data-id="element-144" data-mp="1j92az0hi88ff">
            <AlertTriangleIcon className="h-4 w-4 shrink-0" data-id="element-145" data-mp="26ymwnr51ljse" />
            {missingAllergens} {missingAllergens === 1 ? 'produkt saknar' : 'produkter saknar'} allergendeklaration.
          </p>}

        {grouped.map(({
        section,
        items,
        total
      }, sectionIndex) => <section key={section} id={`del-${sectionIndex}`} className="mt-8 scroll-mt-28" data-id="element-146" data-mp="21ztgfqydfeoj" data-mpi={sectionIndex}>
            <div className="flex items-center justify-between" data-id="element-147" data-mp="221mrrjjxin2v">
              <h3 className="font-display text-xl font-medium text-gray-900" data-id="element-148" data-mp="lk0ddao4fmy1">
                {section} <span className="text-sm font-normal text-gray-400" data-id="element-149" data-mp="qlfz74y4hxhh">{total}</span>
              </h3>
              <button onClick={() => startNew(section)} className="flex items-center gap-1.5 text-xs font-medium text-gray-500 transition-colors hover:text-gray-900" data-id="element-150" data-mp="7uwxhftilnyo">
                <PlusIcon className="h-3.5 w-3.5" data-id="element-151" data-mp="3u1lspnl10ar8" /> Lägg till här
              </button>
            </div>

            {items.length === 0 ? <p className="mt-3 rounded-2xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-400" data-id="element-152" data-mp="qkby595agb2j">
                {query ? 'Inga träffar i den här delen.' : 'Inga produkter ännu.'}
              </p> : <ul className="mt-3 divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white" data-id="element-153" data-mp="gt8xg8g9ty3s">
                {items.map((item, i) => <li key={item.id} className={`flex items-center gap-4 px-4 py-3 ${item.hidden ? 'bg-gray-50' : ''}`} data-id="element-154" data-mp="19b34s2xw5nd8" data-mpi={i}>
                    <div className={`h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-gray-100 ${item.hidden ? 'opacity-50' : ''}`} data-id="element-155" data-mp="14juy3eny4g3r">
                      {item.image && <img src={item.image} alt="" style={item.imageFocus ? {
                objectPosition: item.imageFocus
              } : undefined} className="h-full w-full object-cover" data-id="element-156" data-mp="2s67o218avt78" />}
                    </div>
                    <button onClick={() => setEditing({
              item,
              isNew: false
            })} className="min-w-0 flex-1 text-left" data-id="element-157" data-mp="317f93hlt2xjd">
                      <p className={`truncate text-sm font-medium ${item.hidden ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-158" data-mp="364xkdenapouj">
                        {item.name}
                        {item.hidden && <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600" data-id="element-159" data-mp="3fw1zqfgvvbpw">Dold</span>}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-gray-500" data-id="element-160" data-mp="3jwltra65tz7t">
                        {item.onRequest ? 'Vid förfrågan' : `${formatPrice(item.price)} ${item.unit}`} · {item.category}
                        {item.allergens === undefined ? <span className="text-amber-700" data-id="element-161" data-mp="16imfjg4h8pgf"> · Allergener saknas</span> : item.allergens.length > 0 ? <span data-id="element-162" data-mp="1bh64zqia4jdj"> · {item.allergens.join(', ')}</span> : <span data-id="element-163" data-mp="1fh81ll95qmly"> · Inga allergener</span>}
                      </p>
                    </button>
                    <div className="flex shrink-0 items-center" data-id="element-164" data-mp="1p8kveieinmum">
                      <button className={`${iconButton} hidden sm:flex`} aria-label="Flytta upp" disabled={i === 0 || !!query} onClick={() => platform.moveItem(slug, item.id, -1)} data-id="element-165" data-mp="1paf859fmw8nm">
                        <ArrowUpIcon className="h-3.5 w-3.5" data-id="element-166" data-mp="1z70bzrfx3dpv" />
                      </button>
                      <button className={`${iconButton} hidden sm:flex`} aria-label="Flytta ned" disabled={i === items.length - 1 || !!query} onClick={() => platform.moveItem(slug, item.id, 1)} data-id="element-167" data-mp="236smi9ovftxv">
                        <ArrowDownIcon className="h-3.5 w-3.5" data-id="element-168" data-mp="8uug0fhy5c5o" />
                      </button>
                      <button className={iconButton} aria-label={item.hidden ? 'Visa på sajten' : 'Dölj på sajten'} onClick={() => platform.setItemHidden(slug, item.id, !item.hidden)} data-id="element-169" data-mp="1bbr5sm0uop6">
                        {item.hidden ? <EyeOffIcon className="h-3.5 w-3.5" data-id="element-170" data-mp="3jpkqln58834n" /> : <EyeIcon className="h-3.5 w-3.5" data-id="element-171" data-mp="3jmq2nln0806h" />}
                      </button>
                      <button className={`${iconButton} hidden sm:flex`} aria-label="Duplicera" onClick={() => platform.duplicateItem(slug, item.id)} data-id="element-172" data-mp="k7bl38yk82be">
                        <CopyIcon className="h-3.5 w-3.5" data-id="element-173" data-mp="b7y8i7f52xmg" />
                      </button>
                      <button className={iconButton} aria-label="Ta bort" onClick={() => window.confirm(`Ta bort ${item.name}?`) && platform.deleteItem(slug, item.id)} data-id="element-174" data-mp="6a68sr89677p">
                        <Trash2Icon className="h-3.5 w-3.5" data-id="element-175" data-mp="2r28btz5b4zlw" />
                      </button>
                    </div>
                  </li>)}
              </ul>}
          </section>)}
      </div>

      {editing && <ProductEditor place={partner.place} sections={partner.sections} initial={editing.item} isNew={editing.isNew} onClose={() => setEditing(null)} onSave={item => {
      platform.saveItem(slug, item);
      setEditing(null);
    }} data-id="element-176" data-mp="2jfb8j5ng2chl" />}
    </div>;
}